/**
 * @fileoverview Dashboard opérationnel backoffice.
 *
 * Vue synthétique des métriques et de l'activité paiements.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowRight,
  Banknote,
  CalendarCheck,
  CalendarDays,
  CheckCircle2,
  ReceiptText,
  Scale,
  Timer,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  Input,
  Label,
  MetricCard,
  OperatorLogo,
  PageHeader,
  SectionHeader,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
  formatXof,
  type ChartConfig,
} from '@gnpc/ui';
import { DASHBOARD_METRICS } from '../data/backoffice.ts';

const dashboardActivityData = [
  { date: '2026-07-08', payments: 3580, fines: 5812, failed: 61 },
  { date: '2026-07-09', payments: 3712, fines: 6074, failed: 58 },
  { date: '2026-07-10', payments: 3926, fines: 6551, failed: 63 },
  { date: '2026-07-11', payments: 3488, fines: 5689, failed: 54 },
  { date: '2026-07-12', payments: 3654, fines: 5941, failed: 57 },
  { date: '2026-07-13', payments: 3810, fines: 6237, failed: 49 },
  {
    date: '2026-07-14',
    payments: DASHBOARD_METRICS.paymentsToday,
    fines: 6810,
    failed: Math.round(
      DASHBOARD_METRICS.paymentsToday * ((100 - DASHBOARD_METRICS.successRate24h) / 100),
    ),
  },
];

const DEFAULT_DASHBOARD_PERIOD = {
  start: dashboardActivityData.at(0)?.date ?? '2026-07-08',
  end: dashboardActivityData.at(-1)?.date ?? '2026-07-14',
};

const AVERAGE_PAYMENT_AMOUNT = Math.round(
  DASHBOARD_METRICS.amountCollectedToday / DASHBOARD_METRICS.paymentsToday,
);

const dashboardActivityConfig = {
  payments: {
    label: 'Paiements',
  },
  successful: {
    label: 'Succès',
    color: 'var(--chart-2)',
  },
  failed: {
    label: 'Échecs',
    color: 'var(--chart-5)',
  },
  fines: {
    label: 'Amendes',
    color: 'var(--chart-3)',
  },
} satisfies ChartConfig;

/**
 * Couleurs de MARQUE des opérateurs — volontairement en dur (hex) et non
 * des tokens du DS Quipux : chaque part du donut doit être immédiatement
 * reconnaissable par la couleur commerciale de l'opérateur, comme sur
 * leurs supports officiels.
 */
const paymentMethodConfig = {
  payments: {
    label: 'Paiements',
  },
  orange: {
    label: 'Orange Money',
    color: '#FF7900', // orange signature Orange
  },
  moov: {
    label: 'Moov Money',
    color: '#0066B3', // bleu pur Moov Africa
  },
  mtn: {
    label: 'MTN Money',
    color: '#FFCC00', // jaune officiel MTN
  },
  wave: {
    label: 'Wave',
    color: '#1DC8FF', // bleu ciel Wave
  },
} satisfies ChartConfig;

/**
 * Mapping clé du pie chart → code opérateur OpenAPI, pour afficher le
 * logo officiel (`<OperatorLogo>`) dans la légende de répartition.
 */
const PAYMENT_METHOD_OPERATORS = {
  orange: 'OM_CI',
  moov: 'MOOV_CI',
  mtn: 'MTN_CI',
  wave: 'WAVE_CI',
} as const;

/**
 * Taux de commission (mockés) par opérateur + évolution vs période
 * précédente. Alimente la carte « Commissions par opérateur » (observation
 * Quipux QFS C-06) ; au branchement API, montants et évolution viendront
 * agrégés côté backend.
 */
const OPERATOR_COMMISSION: readonly {
  method: keyof typeof PAYMENT_METHOD_OPERATORS;
  rate: number;
  trend: string;
  direction: 'up' | 'down';
}[] = [
  { method: 'orange', rate: 0.016, trend: '+4,2 %', direction: 'up' },
  { method: 'moov', rate: 0.015, trend: '+1,1 %', direction: 'up' },
  { method: 'mtn', rate: 0.017, trend: '-0,8 %', direction: 'down' },
  { method: 'wave', rate: 0.012, trend: '+2,6 %', direction: 'up' },
];

type DashboardPeriod = typeof DEFAULT_DASHBOARD_PERIOD;
type DashboardActivityKey = 'payments' | 'fines' | 'failed';

const DASHBOARD_ACTIVITY_KEYS: DashboardActivityKey[] = ['payments', 'fines', 'failed'];

function normalizePeriod(period: DashboardPeriod): DashboardPeriod {
  const normalizedPeriod = {
    start: period.start || DEFAULT_DASHBOARD_PERIOD.start,
    end: period.end || DEFAULT_DASHBOARD_PERIOD.end,
  };

  if (normalizedPeriod.start > normalizedPeriod.end) {
    return {
      start: normalizedPeriod.end,
      end: normalizedPeriod.start,
    };
  }

  return normalizedPeriod;
}

function formatShortDate(value: string): string {
  return new Date(`${value}T00:00:00`).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
  });
}

function formatLongDate(value: string): string {
  return new Date(`${value}T00:00:00`).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function formatRate(value: number): string {
  return `${value.toLocaleString('fr-FR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`;
}

function formatCompactXof(value: number): string {
  const millions = value / 1_000_000;

  return `${millions.toLocaleString('fr-FR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} M F CFA`;
}

/**
 * File de traitement affichée dans la bar-list « Files d'attente ».
 * Chaque entrée compare un VOLUME en attente (magnitude) — c'est le job
 * d'une barre horizontale, pas d'une tuile isolée : l'œil compare les
 * longueurs d'un coup au lieu de lire 4 nombres dispersés.
 */
interface TreatmentQueue {
  label: string;
  value: number;
  /** Sévérité — pilote la couleur de barre (status colors du DS). */
  tone: 'danger' | 'warning' | 'neutral';
  /** Détail sous le label (règle métier, montant immobilisé…). */
  detail: string;
  /** Route de la file — la ligne entière devient cliquable. */
  to?: string;
}

const TREATMENT_QUEUES: readonly TreatmentQueue[] = [
  {
    label: 'Encaissés non enregistrés',
    value: DASHBOARD_METRICS.sgiFailedCount,
    tone: 'danger',
    detail: `${formatCompactXof(DASHBOARD_METRICS.sgiFailedAmount)} immobilisés · plus ancien : ${DASHBOARD_METRICS.sgiFailedOldestAgeLabel}`,
    to: '/payments?sgi_status=FAILED',
  },
  {
    label: 'File de rejeu (enreg. Quipux)',
    value: DASHBOARD_METRICS.sgiPendingCount,
    tone: 'warning',
    detail: 'Enregistrements en attente',
    to: '/payments?sgi_status=PENDING',
  },
  {
    label: 'Remboursements (4-eyes)',
    value: DASHBOARD_METRICS.pendingRefundCount,
    tone: 'warning',
    detail: '0,4 M F CFA immobilisés — BO-10',
    to: '/refunds',
  },
  {
    label: 'Exports',
    value: DASHBOARD_METRICS.exportsPending,
    tone: 'neutral',
    detail: 'CSV / XLSX / PDF en génération',
    to: '/exports',
  },
];

/**
 * Couleurs des dash bars de files : segment « rempli » en teinte pleine,
 * reste de la capacité dans la MÊME teinte très atténuée (pastel) — la
 * couleur porte l'identité de la file, l'opacité porte la progression.
 */
const QUEUE_DASH_CLASSES: Record<TreatmentQueue['tone'], { solid: string; pale: string }> = {
  danger: { solid: 'bg-danger', pale: 'bg-danger/15' },
  warning: { solid: 'bg-warning', pale: 'bg-warning/15' },
  neutral: { solid: 'bg-quipux-navy-400', pale: 'bg-quipux-navy-400/15' },
};

/** Nombre de tirets d'une dash bar — constant pour aligner les rangées. */
const QUEUE_DASH_COUNT = 22;

/**
 * Barre de progression en tirets verticaux arrondis (« dash bar »).
 * `ratio` ∈ [0,1] pilote le nombre de tirets pleins ; une file non vide
 * affiche toujours au moins un tiret plein pour rester visible.
 */
function QueueDashBar({
  ratio,
  tone,
}: {
  ratio: number;
  tone: TreatmentQueue['tone'];
}): JSX.Element {
  const filled = ratio > 0 ? Math.max(Math.round(ratio * QUEUE_DASH_COUNT), 1) : 0;
  const classes = QUEUE_DASH_CLASSES[tone];

  return (
    <div aria-hidden="true" className="flex h-5 w-full items-stretch gap-[3px]">
      {Array.from({ length: QUEUE_DASH_COUNT }, (_, index) => (
        <span
          key={index}
          className={cn('flex-1 rounded-full', index < filled ? classes.solid : classes.pale)}
        />
      ))}
    </div>
  );
}

/**
 * Segment d'une répartition « héro + barre segmentée + légende ».
 */
interface BreakdownSegment {
  label: string;
  value: number;
  /** Classe Tailwind du fond — status colors GNPC, jamais couleur seule. */
  colorClass: string;
  /** Montant XOF associé au segment — affiché dans le tooltip. */
  amount: number;
  /**
   * Libellé de la ligne montant du tooltip. Défaut « Montant ». Permet de
   * préciser la sémantique (ex : « Montant non encaissé » pour les échecs,
   * où l'argent n'a jamais été débité).
   */
  amountLabel?: string;
}

interface SegmentedBreakdownProps {
  /** Le TOUT décomposé par les segments — affiché en héro. */
  total: number;
  /** Unité du héro (« paiements », « tâches »…). */
  unit: string;
  segments: readonly BreakdownSegment[];
}

/**
 * Répartition d'un TOUT en états — héro number, barre segmentée aux
 * segments arrondis espacés, puis légende listée (pastille + libellé +
 * valeur alignée à droite).
 *
 * Choix de forme : les segments composent un même total (contrairement à
 * des taux indépendants) — la barre montre les proportions d'un coup
 * d'œil et la légende porte les valeurs exactes. Un segment minuscule
 * (ex : 7 anomalies sur 26 000) garde une largeur minimale : il doit
 * rester VISIBLE, c'est souvent le plus important de la barre.
 */
/**
 * Corps de tooltip d'un segment : volume, montant et part du total.
 * Partagé entre les segments de la barre et les lignes de légende —
 * la ligne de légende offre une cible de survol plus large que le
 * segment fin (règle d'interaction : hit target > mark).
 */
function SegmentTooltipBody({
  segment,
  sum,
}: {
  segment: BreakdownSegment;
  sum: number;
}): JSX.Element {
  const share = sum > 0 ? (segment.value / sum) * 100 : 0;

  return (
    <div className="grid min-w-44 gap-1.5">
      <p className="flex items-center gap-2 font-medium">
        <span
          aria-hidden="true"
          className={cn('size-2.5 shrink-0 rounded-[2px]', segment.colorClass)}
        />
        {segment.label}
      </p>
      <dl className="grid gap-1">
        <div className="flex items-baseline justify-between gap-4">
          <dt className="text-background/70">Volume</dt>
          <dd className="font-semibold tabular-nums">
            {segment.value.toLocaleString('fr-FR')}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-4">
          <dt className="text-background/70">{segment.amountLabel ?? 'Montant'}</dt>
          <dd className="font-semibold tabular-nums">{formatXof(segment.amount)}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-4">
          <dt className="text-background/70">Part</dt>
          <dd className="font-semibold tabular-nums">
            {share.toLocaleString('fr-FR', {
              minimumFractionDigits: share < 1 && share > 0 ? 2 : 1,
              maximumFractionDigits: share < 1 && share > 0 ? 2 : 1,
            })}
            {' %'}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function SegmentedBreakdown({ total, unit, segments }: SegmentedBreakdownProps): JSX.Element {
  const sum = segments.reduce((acc, segment) => acc + segment.value, 0) || 1;

  return (
    <div className="flex flex-col gap-4">
      <p className="flex items-baseline gap-2">
        <span className="text-4xl font-bold tabular-nums tracking-tight text-quipux-navy-950">
          {total.toLocaleString('fr-FR')}
        </span>
        <span className="text-sm text-muted-foreground">{unit}</span>
      </p>

      <div
        className="flex h-7 w-full items-stretch gap-1"
        role="img"
        aria-label={segments
          .map((segment) => `${segment.label} : ${segment.value.toLocaleString('fr-FR')}`)
          .join(' · ')}
      >
        {segments
          .filter((segment) => segment.value > 0)
          .map((segment) => (
            <Tooltip key={segment.label}>
              <TooltipTrigger asChild>
                <div
                  className={cn('min-w-1.5 rounded-md', segment.colorClass)}
                  style={{ width: `${(segment.value / sum) * 100}%` }}
                />
              </TooltipTrigger>
              <TooltipContent side="top">
                <SegmentTooltipBody segment={segment} sum={sum} />
              </TooltipContent>
            </Tooltip>
          ))}
      </div>

      <ul className="grid gap-1">
        {segments.map((segment) => (
          <Tooltip key={segment.label}>
            <TooltipTrigger asChild>
              <li className="flex cursor-default items-center gap-2.5 rounded-md px-1.5 py-1 text-sm transition-colors hover:bg-muted/60">
                <span
                  aria-hidden="true"
                  className={cn('size-3 shrink-0 rounded-[4px]', segment.colorClass)}
                />
                <span className="min-w-0 flex-1 truncate text-quipux-navy-950">
                  {segment.label}
                </span>
                <span className="shrink-0 font-semibold tabular-nums text-quipux-navy-950">
                  {segment.value.toLocaleString('fr-FR')}
                </span>
              </li>
            </TooltipTrigger>
            <TooltipContent side="right">
              <SegmentTooltipBody segment={segment} sum={sum} />
            </TooltipContent>
          </Tooltip>
        ))}
      </ul>
    </div>
  );
}

/**
 * Enveloppe une tuile KPI dans un lien de drill-down cliquable (C-05) — carte
 * entière cliquable, avec liseré au survol/focus, sans casser la hauteur
 * uniforme (`h-full`).
 */
function MetricLink({ to, children }: { to: string; children: ReactNode }): JSX.Element {
  return (
    <Link
      to={to}
      className="group block h-full cursor-pointer rounded-xl outline-none outline-offset-2 transition-[outline-color] hover:outline hover:outline-2 hover:outline-quipux-orange-500/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-quipux-orange-500"
    >
      {children}
    </Link>
  );
}

export function DashboardPage(): JSX.Element {
  const [period, setPeriod] = useState<DashboardPeriod>(DEFAULT_DASHBOARD_PERIOD);
  const [periodDraft, setPeriodDraft] = useState<DashboardPeriod>(DEFAULT_DASHBOARD_PERIOD);

  const selectedPeriod = useMemo(() => normalizePeriod(period), [period]);
  const filteredActivityData = useMemo(
    () =>
      dashboardActivityData.filter(
        (item) => item.date >= selectedPeriod.start && item.date <= selectedPeriod.end,
      ),
    [selectedPeriod],
  );
  const stackedActivityData = useMemo(
    () =>
      filteredActivityData.map((item) => ({
        ...item,
        successful: Math.max(item.payments - item.failed, 0),
      })),
    [filteredActivityData],
  );
  const activityTotals = useMemo(
    () => ({
      payments: filteredActivityData.reduce((total, item) => total + item.payments, 0),
      fines: filteredActivityData.reduce((total, item) => total + item.fines, 0),
      failed: filteredActivityData.reduce((total, item) => total + item.failed, 0),
    }),
    [filteredActivityData],
  );
  const successfulPayments = Math.max(activityTotals.payments - activityTotals.failed, 0);
  // Décomposition des paiements de la période par état de traitement —
  // un TOUT (activityTotals.payments) découpé en 4 états exclusifs, du
  // meilleur au pire. Alimente la barre segmentée « Paiements de la période ».
  const sgiRecordedCount = Math.round(
    successfulPayments * (DASHBOARD_METRICS.sgiRecordedRate / 100),
  );
  const sgiInProgressCount = Math.max(
    successfulPayments - sgiRecordedCount - DASHBOARD_METRICS.sgiFailedCount,
    0,
  );
  // Montants mockés dérivés du panier moyen — au branchement API, chaque
  // segment recevra son montant réel agrégé côté backend.
  const paymentBreakdown: readonly BreakdownSegment[] = [
    {
      label: 'Enregistrés (Quipux)',
      value: sgiRecordedCount,
      colorClass: 'bg-success',
      amount: sgiRecordedCount * AVERAGE_PAYMENT_AMOUNT,
    },
    {
      label: 'Enregistrement en cours',
      value: sgiInProgressCount,
      colorClass: 'bg-warning',
      amount: sgiInProgressCount * AVERAGE_PAYMENT_AMOUNT,
    },
    {
      label: 'Encaissés non enregistrés',
      value: DASHBOARD_METRICS.sgiFailedCount,
      colorClass: 'bg-danger',
      amount: DASHBOARD_METRICS.sgiFailedCount * AVERAGE_PAYMENT_AMOUNT,
      amountLabel: 'Montant à risque',
    },
    {
      label: 'Échecs de paiement',
      value: activityTotals.failed,
      colorClass: 'bg-quipux-navy-300',
      amount: activityTotals.failed * AVERAGE_PAYMENT_AMOUNT,
      // L'argent des échecs n'a JAMAIS été débité — le dire explicitement
      // pour éviter toute confusion avec un montant encaissé.
      amountLabel: 'Montant non encaissé',
    },
  ];
  const periodLabel = `${formatShortDate(selectedPeriod.start)} au ${formatShortDate(
    selectedPeriod.end,
  )}`;
  const paymentMethodData = useMemo(() => {
    const orange = Math.round(activityTotals.payments * 0.386);
    const moov = Math.round(activityTotals.payments * 0.229);
    const mtn = Math.round(activityTotals.payments * 0.179);
    const wave = Math.max(activityTotals.payments - orange - moov - mtn, 0);

    return [
      { method: 'orange', payments: orange, fill: 'var(--color-orange)' },
      { method: 'moov', payments: moov, fill: 'var(--color-moov)' },
      { method: 'mtn', payments: mtn, fill: 'var(--color-mtn)' },
      { method: 'wave', payments: wave, fill: 'var(--color-wave)' },
    ];
  }, [activityTotals.payments]);

  // Suivi financier de l'enregistrement (C-02) : encaissé (paiements
  // confirmés) vs enregistré chez Quipux vs écart. Montants dérivés du
  // panier moyen — au branchement API, agrégés réels côté backend.
  const encaisseAmount = successfulPayments * AVERAGE_PAYMENT_AMOUNT;
  const enregistreAmount = sgiRecordedCount * AVERAGE_PAYMENT_AMOUNT;
  const ecartAmount = Math.max(encaisseAmount - enregistreAmount, 0);

  // Commissions par opérateur (C-06) : volume de l'opérateur × panier moyen
  // × taux de commission, avec évolution vs période précédente.
  const commissionData = useMemo(() => {
    const rows = OPERATOR_COMMISSION.map((operator) => {
      const volume =
        paymentMethodData.find((item) => item.method === operator.method)?.payments ?? 0;
      return {
        ...operator,
        amount: Math.round(volume * AVERAGE_PAYMENT_AMOUNT * operator.rate),
        fill: paymentMethodConfig[operator.method].color ?? '#64748b',
      };
    });
    const maxAmount = Math.max(...rows.map((row) => row.amount), 1);
    const total = rows.reduce((sum, row) => sum + row.amount, 0);
    return { rows, maxAmount, total };
  }, [paymentMethodData]);

  function applyPeriod(): void {
    setPeriod(normalizePeriod(periodDraft));
  }

  function resetPeriod(): void {
    setPeriod(DEFAULT_DASHBOARD_PERIOD);
    setPeriodDraft(DEFAULT_DASHBOARD_PERIOD);
  }

  return (
    <div className="grid gap-6">
      <PageHeader
        eyebrow="Tableau de bord"
        title="Vue d'ensemble opérationnelle"
        actions={
          <Sheet
            onOpenChange={(open) => {
              if (open) {
                setPeriodDraft(selectedPeriod);
              }
            }}
          >
            <SheetTrigger asChild>
              <Button variant="outline">
                <CalendarDays />
                {periodLabel}
              </Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:max-w-md">
              <SheetHeader>
                <SheetTitle>Filtrer la période</SheetTitle>
                <SheetDescription>
                  Sélectionnez la date de début et la date de fin du tableau de bord.
                </SheetDescription>
              </SheetHeader>

              <div className="grid gap-4 px-4">
                <div className="grid gap-2">
                  <Label htmlFor="dashboard-period-start">Date de début</Label>
                  <Input
                    id="dashboard-period-start"
                    type="date"
                    value={periodDraft.start}
                    min={DEFAULT_DASHBOARD_PERIOD.start}
                    max={DEFAULT_DASHBOARD_PERIOD.end}
                    onChange={(event) =>
                      setPeriodDraft((current) => ({
                        ...current,
                        start: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="dashboard-period-end">Date de fin</Label>
                  <Input
                    id="dashboard-period-end"
                    type="date"
                    value={periodDraft.end}
                    min={DEFAULT_DASHBOARD_PERIOD.start}
                    max={DEFAULT_DASHBOARD_PERIOD.end}
                    onChange={(event) =>
                      setPeriodDraft((current) => ({
                        ...current,
                        end: event.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <SheetFooter>
                <SheetClose asChild>
                  <Button type="button" onClick={applyPeriod}>
                    Appliquer
                  </Button>
                </SheetClose>
                <Button type="button" variant="outline" onClick={resetPeriod}>
                  Réinitialiser
                </Button>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        }
      />

      {/* Valeurs absolues (headline numbers) → stat tiles. Les taux (%)
        * vivent dans les gauges radiales ci-dessous ; les files d'attente
        * dans la bar-list « Santé du système ». Une forme par job. */}
      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricLink to="/payments">
          <MetricCard
            label="Nombre de transactions"
            value={activityTotals.payments.toLocaleString('fr-FR')}
            icon={<ReceiptText />}
            tone="neutral"
            className="bg-quipux-navy-100"
          />
        </MetricLink>
        <MetricLink to="/payments">
          <MetricCard
            label="Montant encaissé"
            value={formatCompactXof(encaisseAmount)}
            tone="success"
            icon={<Banknote />}
          />
        </MetricLink>
        <MetricLink to="/payments">
          <MetricCard
            label="Payées avant échéance"
            value={formatRate(DASHBOARD_METRICS.paidBeforeDueRate)}
            icon={<CalendarCheck />}
            tone="info"
          />
        </MetricLink>
        <MetricLink to="/payments?sgi_status=PENDING">
          <MetricCard
            label="Délai moyen enreg. Quipux"
            value={`${DASHBOARD_METRICS.avgSgiRecordDelayMinutes} min`}
            icon={<Timer />}
            tone={
              DASHBOARD_METRICS.sgiRecordDelayP95Minutes >
              DASHBOARD_METRICS.sgiRecordDelayAlertThresholdMinutes
                ? 'danger'
                : 'neutral'
            }
            className={
              DASHBOARD_METRICS.sgiRecordDelayP95Minutes >
              DASHBOARD_METRICS.sgiRecordDelayAlertThresholdMinutes
                ? undefined
                : 'bg-quipux-navy-100'
            }
            footer={{
              title: `p95 ${DASHBOARD_METRICS.sgiRecordDelayP95Minutes} · max ${DASHBOARD_METRICS.sgiRecordDelayMaxMinutes} min`,
            }}
          />
        </MetricLink>
      </section>

      {/* C-02 — Suivi financier de l'enregistrement : encaissé vs enregistré
        * chez Quipux vs écart, plus le taux d'enregistrement réussi. KPI cœur
        * métier demandé par Quipux QFS. */}
      <section className="grid gap-4">
        <SectionHeader title="Suivi financier de l'enregistrement" />
        <div className="grid gap-4 sm:grid-cols-3">
          <MetricLink to="/payments?sgi_status=RECORDED">
            <MetricCard
              label="Montant enregistré Quipux"
              value={formatCompactXof(enregistreAmount)}
              icon={<CheckCircle2 />}
              tone="info"
            />
          </MetricLink>
          <MetricLink to="/reconciliation">
            <MetricCard
              label="Montant en écart"
              value={formatCompactXof(ecartAmount)}
              icon={<Scale />}
              tone="danger"
            />
          </MetricLink>
          <MetricLink to="/reconciliation">
            <MetricCard
              label="Taux d'enregistrement"
              value={formatRate(DASHBOARD_METRICS.sgiRecordedRate)}
              icon={<CalendarCheck />}
              tone="success"
            />
          </MetricLink>
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(20rem,1fr)]">
        <Card className="py-0">
          <CardHeader className="p-0! flex flex-col items-stretch border-b sm:flex-row">
            <div className="flex flex-1 flex-col justify-center gap-1 px-5 pb-3 pt-4 sm:py-0">
              <CardTitle>Évolution des paiements et des amendes</CardTitle>
              <CardDescription>
                Volume quotidien : paiements (barres, succès + échecs) et amendes associées (ligne).
              </CardDescription>
            </div>
            <div className="flex">
              {DASHBOARD_ACTIVITY_KEYS.map((key) => (
                <div
                  key={key}
                  className="relative z-10 flex flex-1 flex-col justify-center gap-1 border-t px-4 py-4 text-left sm:min-w-28 sm:border-l sm:border-t-0"
                >
                  <span className="text-muted-foreground text-xs">
                    {dashboardActivityConfig[key].label}
                  </span>
                  <span className="text-quipux-navy-950 text-lg font-semibold tabular-nums leading-none">
                    {activityTotals[key].toLocaleString('fr-FR')}
                  </span>
                </div>
              ))}
            </div>
          </CardHeader>
          <CardContent className="px-2 pb-5 pt-3 sm:px-6 sm:pb-6 sm:pt-4">
            <ChartContainer
              config={dashboardActivityConfig}
              className="aspect-auto h-[260px] w-full"
            >
              <ComposedChart
                accessibilityLayer
                data={stackedActivityData}
                margin={{ left: 12, right: 12 }}
              >
                <CartesianGrid vertical={false} />
                <YAxis hide />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={32}
                  tickFormatter={(value: string) => formatShortDate(value)}
                />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      className="w-44"
                      labelFormatter={(value) => formatLongDate(String(value))}
                    />
                  }
                />
                <Bar
                  dataKey="successful"
                  stackId="payments"
                  fill="var(--color-successful)"
                  radius={[0, 0, 4, 4]}
                  maxBarSize={72}
                />
                <Bar
                  stackId="payments"
                  dataKey="failed"
                  fill="var(--color-failed)"
                  radius={[4, 4, 0, 0]}
                  minPointSize={4}
                  maxBarSize={72}
                />
                <Line
                  type="monotone"
                  dataKey="fines"
                  stroke="var(--color-fines)"
                  strokeWidth={3}
                  dot={{ fill: 'var(--color-fines)', r: 3 }}
                  activeDot={{ r: 5 }}
                />
                {/* Légende : rend lisible ce que chaque couleur représente
                  * (succès / échecs empilés = paiements ; ligne = amendes). */}
                <ChartLegend content={<ChartLegendContent />} />
              </ComposedChart>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card className="flex flex-col py-0">
          <CardHeader className="gap-1 px-5 pb-2 pt-4">
            <CardTitle>Répartition par moyen de paiement</CardTitle>
            <CardDescription>{periodLabel}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col px-3 pb-4 pt-0">
            <ChartContainer
              config={paymentMethodConfig}
              className="mx-auto aspect-square max-h-[240px] w-full"
            >
              <PieChart accessibilityLayer>
                <ChartTooltip
                  content={<ChartTooltipContent hideLabel nameKey="method" />}
                />
                <Pie
                  data={paymentMethodData}
                  dataKey="payments"
                  nameKey="method"
                  innerRadius={54}
                  outerRadius={90}
                  paddingAngle={2}
                  strokeWidth={0}
                />
              </PieChart>
            </ChartContainer>
            {/* Légende custom avec logos officiels des opérateurs — plus
              * riche que la ChartLegend générique : logo + libellé + volume
              * + part de marché. La pastille couleur relie visuellement
              * chaque ligne à sa part du donut. */}
            <ul className="mt-1 grid gap-1.5 px-2">
              {paymentMethodData.map((item) => {
                const share =
                  activityTotals.payments > 0
                    ? Math.round((item.payments / activityTotals.payments) * 100)
                    : 0;
                return (
                  <li
                    key={item.method}
                    className="flex items-center justify-between gap-2 rounded-md px-2 py-1"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        aria-hidden="true"
                        className="size-2 shrink-0 rounded-full"
                        style={{ backgroundColor: item.fill }}
                      />
                      <OperatorLogo
                        code={
                          PAYMENT_METHOD_OPERATORS[
                          item.method as keyof typeof PAYMENT_METHOD_OPERATORS
                          ]
                        }
                        size="sm"
                        withLabel
                      />
                    </span>
                    <span className="shrink-0 text-sm tabular-nums text-quipux-navy-950">
                      {item.payments.toLocaleString('fr-FR')}
                      <span className="ml-1.5 text-xs text-muted-foreground">{share} %</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      </section>

      {/* C-06 — Commissions par opérateur : montant sur la période + évolution
        * vs période précédente. Barres horizontales = comparaison de magnitude. */}
      <section className="grid gap-4">
        <Card className="py-0">
          <CardHeader className="gap-1 px-5 pb-2 pt-4">
            <CardTitle>Commissions par opérateur</CardTitle>
            <CardDescription>
              Total {formatCompactXof(commissionData.total)} · évolution vs période précédente.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 px-5 pb-5 pt-2 sm:grid-cols-2">
            {commissionData.rows.map((row) => (
              <div key={row.method} className="grid gap-1.5">
                <div className="flex items-center justify-between gap-3">
                  <OperatorLogo code={PAYMENT_METHOD_OPERATORS[row.method]} size="sm" withLabel />
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="text-sm font-semibold tabular-nums text-quipux-navy-950">
                      {formatCompactXof(row.amount)}
                    </span>
                    <Badge
                      variant="outline"
                      className={cn(
                        'tabular-nums',
                        row.direction === 'up'
                          ? 'border-status-paid/30 bg-status-paid/10 text-status-paid-800'
                          : 'border-danger/30 bg-danger/10 text-status-failed-800',
                      )}
                    >
                      {row.trend}
                    </Badge>
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${(row.amount / commissionData.maxAmount) * 100}%`,
                      backgroundColor: row.fill,
                    }}
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>

      {/* ------------------------------------------------------------------
        * SANTÉ DU SYSTÈME — la rangée que l'agent doit lire EN PREMIER.
        * Couvre les métriques critiques de la spec (DashboardMetrics) :
        * encaissé non enregistré (invariant n°1), file de rejeu STI,
        * remboursements 4-eyes, exports — plus la santé de la chaîne aval
        * (taux + délai d'enregistrement STI). La supervision des connecteurs
        * externes (BO-05) vit désormais sur la page dédiée /integrations.
        * ---------------------------------------------------------------- */}
      <section className="grid gap-4">
        <SectionHeader
          title="Santé du système"
          description="Chaîne d'enregistrement Quipux et files d'attente."
        />
        <div className="grid gap-4 xl:grid-cols-2">
          {/* Décomposition d'un TOUT (paiements de la période) → héro +
            * barre segmentée + légende listée. Les taux de succès et
            * d'enregistrement STI se LISENT dans les proportions. */}
          <Card className="flex flex-col py-0">
            <CardHeader className="gap-1 px-5 pb-0 pt-4">
              <CardTitle>Paiements de la période</CardTitle>
              <CardDescription>Répartition par état de traitement.</CardDescription>
            </CardHeader>
            <CardContent className="flex-1 px-5 pb-5 pt-3">
              <SegmentedBreakdown
                total={activityTotals.payments}
                unit="paiements"
                segments={paymentBreakdown}
              />
            </CardContent>
          </Card>

          {/* Files d'attente → rangées « héro + dash bar » : compteur en
            * gros à gauche, titre + barre de tirets à droite. Ratio des
            * tirets pleins = poids relatif de la file (vs la plus longue). */}
          <Card className="py-0">
            <CardHeader className="gap-1 px-5 pb-0 pt-4">
              <CardTitle>Files de traitement</CardTitle>
              <CardDescription>Éléments en attente d'action ou de rejeu.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-1.5 px-3 pb-4 pt-2">
              {(() => {
                const max = Math.max(...TREATMENT_QUEUES.map((q) => q.value), 1);
                return TREATMENT_QUEUES.map((queue) => {
                  const row = (
                    <>
                      <div className="w-12 shrink-0 pt-0.5">
                        <p className="text-3xl font-bold tabular-nums tracking-tight text-quipux-navy-950">
                          {queue.value}
                        </p>
                      </div>
                      <div className="grid min-w-0 flex-1 gap-1.5">
                        <div className="flex items-center justify-between gap-3">
                          <span className="min-w-0 truncate text-sm font-semibold text-quipux-navy-950">
                            {queue.label}
                          </span>
                          {queue.to ? (
                            <ArrowRight className="size-3.5 shrink-0 text-muted-foreground" />
                          ) : null}
                        </div>
                        <QueueDashBar ratio={queue.value / max} tone={queue.tone} />
                        <p className="truncate text-xs text-muted-foreground">{queue.detail}</p>
                      </div>
                    </>
                  );

                  return queue.to ? (
                    <Link
                      key={queue.label}
                      to={queue.to}
                      aria-label={`Ouvrir la file : ${queue.label} (${queue.value})`}
                      className="flex gap-3 rounded-md px-2 py-2 transition-colors hover:bg-muted/60"
                    >
                      {row}
                    </Link>
                  ) : (
                    <div key={queue.label} className="flex gap-3 px-2 py-2">
                      {row}
                    </div>
                  );
                });
              })()}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
